import {useContext} from "react";
import {Tooltip} from "@mui/material";
import {HelpOutline, Visibility, VisibilityOff} from "@mui/icons-material";
import {CheckboxEl} from "../ShopTableCol";
import {MultiStateCheckboxEl} from "./MultiStateCheckboxEl";
import {TablePersonalizeContext} from "../../../../misc/scroller/TablePersonalize";
import {aluminumDoor, aluminumFrame, existingDoor, existingFrame} from "../../quote/openings/Const";

// structural row type so this module can be shared by both Opening and QuoteOpening
// without importing either (avoids circular imports with QuoteOpenings.tsx).
// A null by-others means "inherit from the series' supplier category"; a non-null
// value is an explicit per-opening override. The *Calc fields are the effective
// value resolved by the backend (override when set, otherwise inherited).
type ByOthersRow = {
    openingType: string;
    doorSeries?: string;
    frameSeries?: string;
    doorActiveByOthers: boolean | null;
    doorInactiveByOthers: boolean | null;
    frameByOthers: boolean | null;
    doorActiveByOthersCalc: boolean;
    doorInactiveByOthersCalc: boolean;
    frameByOthersCalc: boolean;
};

// A series change discards any explicit override so the opening re-inherits its
// by-others from the new series' supplier category.
export function applyDoorSeriesByOthers(row: ByOthersRow, doorSeries: string) {
    row.doorActiveByOthers = null;
    row.doorInactiveByOthers = null;
}

export function applyFrameSeriesByOthers(row: ByOthersRow, frameSeries: string) {
    row.frameByOthers = null;
}

// Copy/paste of a series cell carries the row's by-others flags alongside the
// series value, so a pasted row keeps whatever by-others state the source had
// (including manual overrides) rather than re-deriving it from the series.
// Plain-text pastes (e.g. from outside the app) fall back to treating the value
// as a bare series string and deriving the flags.
export function copyDoorSeries(row: ByOthersRow): string {
    return JSON.stringify({
        doorSeries: row.doorSeries || "",
        doorActiveByOthers: row.doorActiveByOthers,
        doorInactiveByOthers: row.doorInactiveByOthers,
    });
}

export function pasteDoorSeries(row: ByOthersRow, value: string) {
    try {
        const obj = JSON.parse(value) as {doorSeries: string; doorActiveByOthers: boolean | null; doorInactiveByOthers: boolean | null};
        row.doorSeries = obj.doorSeries;
        row.doorActiveByOthers = obj.doorActiveByOthers ?? null;
        row.doorInactiveByOthers = obj.doorInactiveByOthers ?? null;
    } catch (e) {
        row.doorSeries = value;
        applyDoorSeriesByOthers(row, value);
    }
}

export function copyFrameSeries(row: ByOthersRow): string {
    return JSON.stringify({
        frameSeries: row.frameSeries || "",
        frameByOthers: row.frameByOthers,
    });
}

export function pasteFrameSeries(row: ByOthersRow, value: string) {
    try {
        const obj = JSON.parse(value) as {frameSeries: string; frameByOthers: boolean | null};
        row.frameSeries = obj.frameSeries;
        row.frameByOthers = obj.frameByOthers ?? null;
    } catch (e) {
        row.frameSeries = value;
        applyFrameSeriesByOthers(row, value);
    }
}

// Shared personalization flag controlling visibility of the by-others checkboxes
// on both Frame Series and Door Series columns. Defaults to shown.
export const showByOthersFlag = "showByOthers";

export function useShowByOthers() {
    const ctx = useContext(TablePersonalizeContext);
    const show = ctx.flags[showByOthersFlag] ?? true;
    return {
        show,
        toggle: () => ctx.setFlag(showByOthersFlag, !show),
    };
}

function ByOthersHeaderToggle() {
    const {show, toggle} = useShowByOthers();

    return (
        <Tooltip title={show ? "Hide 'by others' checkboxes" : "Show 'by others' checkboxes"}>
            <div
                onClick={e => {
                    e.stopPropagation();
                    toggle();
                }}
                style={{cursor: "pointer", fontSize: "0.9rem", display: "flex", alignItems: "center"}}
            >
                {show ? <Visibility fontSize="inherit"/> : <VisibilityOff fontSize="inherit"/>}
            </div>
        </Tooltip>
    );
}

export function FrameSeriesHeader() {
    return (
        <div style={{display: "flex", width: "100%", alignItems: "center", gap: 4}}>
            <div style={{flex: 1}}>Frame Series</div>
            <Tooltip title="By Others">
                <div style={{fontSize: "0.9rem", display: "flex", alignItems: "center"}}>
                    <HelpOutline fontSize="inherit"/>
                </div>
            </Tooltip>
            <ByOthersHeaderToggle/>
        </div>
    );
}

export function DoorSeriesHeader() {
    return (
        <div style={{display: "flex", width: "100%", alignItems: "center", gap: 4}}>
            <div style={{flex: 1}}>Door Series</div>
            <Tooltip title="Active By Others / Inactive By Others">
                <div style={{fontSize: "0.9rem", display: "flex", alignItems: "center"}}>
                    <HelpOutline fontSize="inherit"/>
                </div>
            </Tooltip>
            <ByOthersHeaderToggle/>
        </div>
    );
}

export function FrameByOthersCell(props: {data: ByOthersRow}) {
    const {show} = useShowByOthers();
    if (!show) return null;

    const data = props.data;
    // aluminum and existing frames are always supplied externally, so the by-others
    // flag is fixed and can't be overridden
    const disabled = data.frameSeries === aluminumFrame || data.frameSeries === existingFrame;
    return (
        <CheckboxEl
            style={{position: "absolute", top: 0, right: 2, width: 20, zIndex: 2}}
            checked={data.frameByOthersCalc}
            disabled={disabled}
            onChange={checked => Object.assign({}, data, {
                frameByOthers: checked,
            })}
        />
    );
}

export function DoorByOthersCell(props: {data: ByOthersRow}) {
    const {show} = useShowByOthers();
    if (!show) return null;

    const data = props.data;
    // aluminum and existing doors are always supplied externally, so the by-others
    // flag is fixed and can't be overridden
    const disabled = data.doorSeries === aluminumDoor || data.doorSeries === existingDoor;
    if (data.openingType === "Pair") {
        return (
            <MultiStateCheckboxEl
                style={{position: "absolute", top: 0, right: 2, width: 20, zIndex: 2}}
                labelA="Active By Others"
                checkedA={data.doorActiveByOthersCalc}
                labelB="Inactive By Others"
                checkedB={data.doorInactiveByOthersCalc}
                disabled={disabled}
                onChange={(a, b) => Object.assign({}, data, {
                    doorActiveByOthers: a,
                    doorInactiveByOthers: b,
                })}
            />
        );
    }

    return (
        <CheckboxEl
            style={{position: "absolute", top: 0, right: 2, width: 20, zIndex: 2}}
            checked={data.doorActiveByOthersCalc}
            disabled={disabled}
            onChange={checked => Object.assign({}, data, {
                doorActiveByOthers: checked,
                doorInactiveByOthers: checked,
            })}
        />
    );
}
